import type { MessageSurface } from "@/analytics/surfaces";
import { useFlowSessionById } from "@/api/flow_sessions";
import { useFlowMessages } from "@/api/messages";
import ChatHistory from "@/components/ChatHistory";
import { MessageInput, MessageInputRef } from "@/components/MessageInput";
import { usePermissionModeControl } from "@/components/MessageInput/hooks/usePermissionModeControl";
import { MessageInputControlSet } from "@/constants/message-input";
import { useOnDrop } from "@/dnd/OsFileDropLayer";
import { useMessageActions } from "@/hooks/useMessageActions";
import {
  tierOf,
  useReasoningEffort,
} from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { useEventStream } from "@/providers/EventStreamProvider";
import { mergeMessageAndCanvasReferencedEntities } from "@/utils/mergeReferencedEntities";
import {
  FlowSession,
  PostMessageParams,
  ReferencedEntity,
} from "@cohesive/api-client";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useResizeObserver } from "usehooks-ts";
import FollowUp, { FollowUpHandle } from "./FollowUp";

import { useSessionContext } from "@/context/SessionContext";
import { cn } from "@/utils";
import { ErrorBoundary } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Button } from "./ui/button";

// Breathing room left above the user message when it is anchored to the top.
const SCROLL_TOP_OFFSET = 16;

const ChatArea: React.FC<{
  /** Where this chat lives — ChatArea is embedded in several hosts, so the
   *  host names the surface (retries emit Message Sent through this hook). */
  surface: MessageSurface;
  followUpsEnabled?: boolean;
  className?: string;
  location?: "sidebar" | "main";
  controlSet?: MessageInputControlSet;
  placeholder?: string;
  hideMessageInput?: boolean;
  referencedEntities?: ReferencedEntity[];
  onSendSuccess?: (
    sentReferencedEntities: ReferencedEntity[] | undefined,
  ) => void;
}> = ({
  surface,
  className,
  followUpsEnabled = true,
  location,
  controlSet = MessageInputControlSet.DEFAULT,
  placeholder = "How can Cohesive help you today?",
  hideMessageInput = false,
  referencedEntities,
  onSendSuccess,
}) => {
  const { sessionId } = useSessionContext();
  const queryClient = useQueryClient();

  const isWebSocketReceivingMessages = useRef<boolean>(false);
  const followUpRef = useRef<FollowUpHandle>(null);
  const messageInputRef = useRef<MessageInputRef>(null);
  const chatHistoryRef = useRef<HTMLDivElement>(document.createElement("div"));
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement>(document.createElement("div"));

  // Tracks how far the scroll position is from the bottom; used to keep the
  // viewport stable when older messages are prepended by infinite scroll.
  const distanceToBottomRef = useRef<number>(0);
  const prevFirstMessageIdRef = useRef<string | undefined>(undefined);
  const prevLastUserMessageIdRef = useRef<string | undefined>(undefined);
  const didInitialScrollRef = useRef<boolean>(false);

  const [scrolledToBottom, setScrolledToBottom] = useState<boolean>(true);
  const [emptyMessage, setEmptyMessage] = useState<boolean>(true);

  // Reset session-tracking refs in a layout effect (not a passive effect) so
  // the reset runs before the scroll-management useLayoutEffect below. When
  // switching to a session with cached messages, sessionId and flowMessages
  // change in the same commit; if this were a useEffect it would run after the
  // scroll logic, which would then see stale refs and misread the new session's
  // messages as a "new user message" instead of an initial load.
  useLayoutEffect(() => {
    didInitialScrollRef.current = false;
    prevFirstMessageIdRef.current = undefined;
    prevLastUserMessageIdRef.current = undefined;
  }, [sessionId]);

  const { data: flowSession } = useFlowSessionById(sessionId ?? "", {
    enabled: !!sessionId,
  });
  // The composer's Cohesive tier, keyed like the full-page chat's so a chat keeps one tier
  // across surfaces.
  const [reasoningEffort, setReasoningEffort] = useReasoningEffort(
    `chat_reasoning_effort_${sessionId ?? "draft"}`,
    tierOf(flowSession?.reasoning_effort),
  );

  const { data: currentFlowSession } = useFlowSessionById(sessionId || "", {
    enabled: !!sessionId,
  });

  const {
    data: flowMessages = [],
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isLoading,
  } = useFlowMessages(sessionId || "", undefined, {
    enabled: !!sessionId && !isWebSocketReceivingMessages.current,
  });

  const [infiniteRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading || isFetchingNextPage,
    hasNextPage,
    onLoadMore: () => fetchNextPage(),
    rootMargin: "400px 0px 0px 0px",
  });

  const rootRefSetter = useCallback(
    (node: HTMLDivElement) => {
      rootRef(node);
      chatHistoryRef.current = node;
    },
    [rootRef],
  );

  const lastUserMessage = useMemo(() => {
    return flowMessages.filter((message) => message.origin === "user").pop();
  }, [flowMessages]);

  const calculateScrolledToBottom = useCallback(() => {
    const el = chatHistoryRef.current;
    if (!el) return false;

    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
    setScrolledToBottom((prev) => (prev === isAtBottom ? prev : isAtBottom));

    return isAtBottom;
  }, []);

  // Sizes the bottom spacer so the region from the last user message down is
  // always at least one viewport tall. This lets the user message reach the
  // top even before the assistant has streamed enough content to fill the
  // space, then the spacer shrinks toward 0 as content grows in.
  const updateSpacer = useCallback(() => {
    const el = chatHistoryRef.current;
    const spacer = spacerRef.current;
    const userMessage = lastUserMessageRef.current;
    if (!el || !spacer) return;

    if (!userMessage) {
      spacer.style.minHeight = "0px";
      return;
    }

    const currentSpacer = spacer.offsetHeight;
    const naturalHeight = el.scrollHeight - currentSpacer;
    const containerTop = el.getBoundingClientRect().top;
    const userTop =
      userMessage.getBoundingClientRect().top - containerTop + el.scrollTop;

    const desiredHeight = Math.max(
      0,
      userTop - SCROLL_TOP_OFFSET + el.clientHeight - naturalHeight,
    );
    spacer.style.minHeight = `${desiredHeight}px`;
  }, []);

  const scrollLastUserMessageToTop = useCallback(() => {
    const el = chatHistoryRef.current;
    const userMessage = lastUserMessageRef.current;
    if (!el || !userMessage) return;

    const containerTop = el.getBoundingClientRect().top;
    const userTop =
      userMessage.getBoundingClientRect().top - containerTop + el.scrollTop;

    el.scrollTo({
      top: Math.max(0, userTop - SCROLL_TOP_OFFSET),
      behavior: "smooth",
    });
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = chatHistoryRef.current;
    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight - el.clientHeight,
      behavior: "smooth",
    });
  }, []);

  const onScroll = useCallback(() => {
    const el = chatHistoryRef.current;
    if (!el) return;

    distanceToBottomRef.current = el.scrollHeight - el.scrollTop;
    calculateScrolledToBottom();
  }, [calculateScrolledToBottom]);

  useLayoutEffect(() => {
    const el = chatHistoryRef.current;
    if (!el) return;

    updateSpacer();

    const firstId = flowMessages[0]?.id;
    const lastUserId = lastUserMessage?.id;

    // Initial load for this session: land at the bottom.
    if (!didInitialScrollRef.current) {
      if (flowMessages.length > 0) {
        el.scrollTop = el.scrollHeight;
        didInitialScrollRef.current = true;
        prevFirstMessageIdRef.current = firstId;
        prevLastUserMessageIdRef.current = lastUserId;
      }
      return;
    }

    // Older messages prepended (infinite scroll): keep the viewport stable.
    if (
      firstId !== prevFirstMessageIdRef.current &&
      lastUserId === prevLastUserMessageIdRef.current
    ) {
      el.scrollTop = el.scrollHeight - distanceToBottomRef.current;
      prevFirstMessageIdRef.current = firstId;
      return;
    }

    // A new user message was sent: anchor it to the top.
    if (lastUserId !== prevLastUserMessageIdRef.current) {
      scrollLastUserMessageToTop();
      prevFirstMessageIdRef.current = firstId;
      prevLastUserMessageIdRef.current = lastUserId;
      return;
    }

    // Otherwise (assistant streaming, etc.): never auto-scroll.
  }, [flowMessages, lastUserMessage, updateSpacer, scrollLastUserMessageToTop]);

  const onContentOrViewportResize = useCallback(() => {
    updateSpacer();
    calculateScrolledToBottom();
  }, [updateSpacer, calculateScrolledToBottom]);

  // Viewport height changes (clientHeight) affect the spacer math.
  useResizeObserver({
    ref: chatHistoryRef,
    onResize: onContentOrViewportResize,
  });

  // Content height changes (streaming tokens, thinking-block collapse, etc.)
  // must keep the spacer in lockstep so the scroll position never gets clamped
  // and the anchored user message stays put. The collapse animates `height`
  // via inline style, which a childList MutationObserver would miss -- a
  // ResizeObserver on the content fires on every frame of that animation.
  useResizeObserver({
    ref: contentRef,
    onResize: onContentOrViewportResize,
  });

  const {
    uploadedFiles,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
    onSendMessage,
    isLoading: isMessageActionLoading,
  } = useMessageActions({
    surface,
    onMessageSent: (data) => {
      queryClient.setQueryData(
        ["flowSession", data.flowSession.id],
        (prev: FlowSession) => {
          if (prev.follow_up_data) {
            prev.follow_up_data.messages = [];
          }
          return prev;
        },
      );

      followUpRef.current?.setFollowUpMessages([]);

      onSendSuccess?.(data.referenced_entities);
    },
  });

  // Shares the composer's pending pick, so a retry stamps it too.
  const permissionModeControl = usePermissionModeControl({
    flowSessionId: sessionId,
  });

  const onRetrySend = useCallback(
    (params: PostMessageParams) =>
      onSendMessage(
        {
          ...params,
          referenced_entities: mergeMessageAndCanvasReferencedEntities(
            params.referenced_entities,
            referencedEntities ?? [],
          ),
          permission_mode: permissionModeControl.sendPermissionMode,
        },
        { inputMethod: "retry" },
      ),
    [
      onSendMessage,
      referencedEntities,
      permissionModeControl.sendPermissionMode,
    ],
  );

  // Only own the page drop target when ChatArea renders its own composer.
  // When `hideMessageInput` is set, a parent supplies the visible MessageInput
  // (and its own useMessageActions queue), so the parent registers the drop
  // handler — otherwise dropped files land on this internal hidden queue and
  // never appear on the composer the user is actually typing in.
  const onOsFileDrop = useCallback(
    (files: File[] | null) => onAttachFile(files, { method: "drop_os" }),
    [onAttachFile],
  );
  useOnDrop(hideMessageInput ? undefined : onOsFileDrop);

  const { registerHandler } = useEventStream();

  useEffect(() => {
    return registerHandler((message) => {
      if (message.type === "flow_message.content_delta") {
        if (
          message.data.flow_message_content_delta.flow_session_id !== sessionId
        )
          return;

        // Manage the streaming state ref for this component
        if (message.data.flow_message_content_delta.done) {
          isWebSocketReceivingMessages.current = false;

          // there is a slight delay between the message finishing and the usage limit being updated
          // so we need to wait a bit before refetching the userInfo
          setTimeout(() => {
            queryClient.refetchQueries({
              queryKey: ["userInfo"],
            });
          }, 1000);
        } else {
          isWebSocketReceivingMessages.current = true;
        }
      }
    });
  }, [registerHandler, sessionId]);

  // When the parent supplies the visible composer (`hideMessageInput`), this
  // wrapper only hosts the absolute scroll-to-bottom button — drop the `mb-6`
  // so it adds no gap between the history and the parent's stable input.
  const chatInputClassName = hideMessageInput
    ? `w-full sm:max-w-[776px] mx-auto`
    : `w-full sm:max-w-[776px] mx-auto mb-6`;
  const lastMessage = useMemo(() => {
    return flowMessages?.[flowMessages.length - 1];
  }, [flowMessages]);

  const disableSendButton = useCallback(() => {
    if (lastUserMessage?.status === "cancelled") {
      return false;
    }
    if (isWebSocketReceivingMessages.current) {
      return true;
    }
    if (isMessageActionLoading) {
      return true;
    }
    if (lastMessage?.status === "pending") {
      return true;
    }
    if (lastMessage?.status === "streaming") {
      return true;
    }
    if (flowSession?.status !== "ready") {
      return true;
    }
    return false;
  }, [
    isMessageActionLoading,
    lastMessage?.status,
    lastUserMessage?.status,
    flowSession?.status,
  ]);

  return (
    <div
      id="chat-area"
      className={cn(
        "flex flex-col transition-all duration-300 h-full relative z-9",
        className,
      )}
    >
      <div
        ref={rootRefSetter}
        onScroll={onScroll}
        id="chat-history"
        className={cn(
          // overflow-x-hidden: when only overflow-y is set the browser computes
          // overflow-x as auto, letting wide content (tables, code) scroll the
          // whole column sideways. Clamp it so the column can't overflow; wide
          // tables scroll inside their own overflow-auto wrapper instead.
          "relative flex-1 min-h-0 w-full px-0 overflow-y-auto overflow-x-hidden no-scrollbar transition-all duration-300",
          // The 50px clearance keeps the last message off the floating composer
          // overlap in `main`; when the parent owns the input below us, the
          // history should run flush to its top instead.
          hideMessageInput ? "pb-2" : "pb-[50px]",
        )}
      >
        <div ref={contentRef}>
          <div ref={infiniteRef} className="h-1" />

          <ChatHistory
            messages={flowMessages || []}
            lastUserMessageRef={lastUserMessageRef}
            onRetry={onRetrySend}
            isSessionReady={flowSession?.status === "ready"}
          />
        </div>

        <div ref={spacerRef} aria-hidden="true" />
      </div>

      {!followUpsEnabled && (
        <ErrorBoundary>
          <FollowUp
            ref={followUpRef}
            followUpData={currentFlowSession?.follow_up_data}
            flowSessionId={sessionId!}
            showFollowUps={
              scrolledToBottom &&
              emptyMessage &&
              !isWebSocketReceivingMessages.current &&
              lastMessage?.origin !== "user"
            }
            onMessageClick={async (message) => {
              messageInputRef?.current?.setValue(message.content);
            }}
          />
        </ErrorBoundary>
      )}

      <div className={cn("relative", chatInputClassName)}>
        <AnimatePresence>
          {!scrolledToBottom && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: 1,
                opacity: 1,
                transition: { delay: 0.25 },
              }}
              exit={{ scale: 0, opacity: 0 }}
              className="absolute w-10 h-10 left-1/2 transform -translate-x-1/2"
              style={{
                top: -50,
              }}
            >
              <Button
                onClick={() => scrollToBottom()}
                size="icon"
                variant="outline"
                className="h-10 w-10 rounded-full shadow-md"
                aria-label="Scroll to bottom"
              >
                <ArrowDown className="h-5 w-5 stroke-[3px]" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {!hideMessageInput && (
          <MessageInput
            sendButtonDisabled={disableSendButton()}
            ref={messageInputRef}
            onSend={onSendMessage}
            referencedEntities={referencedEntities}
            onAttachFile={onAttachFile}
            onAttachFileRecords={onAttachFileRecords}
            onMessageChange={(message: string) => {
              if (message.trim() === "") {
                setEmptyMessage(true);
              } else {
                setEmptyMessage(false);
              }
            }}
            onRemoveFile={onRemoveFile}
            attachedFiles={uploadedFiles}
            autoFocus
            location={location}
            flowSessionId={sessionId}
            controlSet={controlSet}
            agentReasoningEffort={reasoningEffort}
            onAgentReasoningEffortChange={setReasoningEffort}
            placeholder={placeholder}
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(ChatArea);
